/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiIcon, EuiSpacer, EuiText } from '@elastic/eui'
import { Tooltip, TooltipTable } from '@elastic/charts'

import { truncateToFirstNonZero } from '@modules/autoops-lib/util'
import { type DateRangeOption, timeFormatterFn } from '@modules/autoops-lib/constants'

const messages = defineMessages({
  disclaimer: {
    id: `project.charts.disclaimer`,
    defaultMessage: `The selected time range does not include this entire bucket. It might contain partial data.`,
  },
})

export const TruncatedTooltip = ({
  units,
  range,
  max,
  hasDisclaimer = true,
}: {
  units: string
  range: DateRangeOption
  max: number
  hasDisclaimer?: boolean
}) => {
  const { formatMessage } = useIntl()

  return (
    <Tooltip
      headerFormatter={({ value }) => {
        const formattedValue = timeFormatterFn(value, range)

        if (hasDisclaimer && value === max) {
          return (
            <Fragment>
              <EuiFlexGroup alignItems='center' responsive={false} gutterSize='xs'>
                <EuiFlexItem grow={false}>
                  <EuiIcon type='info' />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='xs'>{formatMessage(messages.disclaimer)}</EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size='xs' />
              {formattedValue}
            </Fragment>
          )
        }

        return formattedValue
      }}
      body={({ items }) => (
        <TooltipTable
          columns={[
            {
              id: 'color',
              type: 'color',
            },
            {
              id: 'label',
              type: 'custom',
              cell: ({ label }) => label,
            },
            {
              id: 'value',
              type: 'custom',
              style: { textAlign: 'right' },
              cell: ({ value }) => `${truncateToFirstNonZero(Number(value))} ${units}`,
            },
          ]}
          items={items}
        />
      )}
    />
  )
}
